import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation, Trans } from "react-i18next";

import color from "color";
import { styled } from "@mui/material/styles";

import Typography from "@mui/material/Typography";

import {
  Routes,
  Route,
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";

import { motion } from "framer-motion";

import { client } from "../../bit/board/hooks/useGraphQLClient";
// import usePageTracking from "../../bit/tracking/usePageTracking";
// import useEventTracking from "../../bit/tracking/useEventTracking";

import { QUERY_ACCESS_EVENT } from "../../config/graphql/query";

import useIdleEffect from "../../lib/hooks/useIdleEffect";

import Title from "../../components/Title";
import BackButton from "../../components/BackButton";
import Curve from "../../components/Animations/PageTransitions/Curve";

import Message from "./Message";
import Calling from "../Calling";
import VideoCalling from "./VideoCalling";
import Bump from "../../components/Animations/Bump";

export const Status = {
  Connecting: "connecting",
  Ringing: "ringing",
  Open: "open",
  Error: "error",
  Close: "close",
};

const ContactButton = styled(motion.button)(({ theme }) => ({
  margin: "0 2.5rem",
  minWidth: "16.4rem",
  padding: "1.5rem",
  borderRadius: "1rem",
  background: theme.palette.primary.main,

  h3: {
    color: theme.palette.background.default,
  },

  "&:disabled": {
    backgroundColor: color(theme.palette.primary.main).lighten(0.4).hex(),
  },
}));

type NamespaceType = keyof Required<IMemberEmployeePreferences>;

function getPreference(
  receiver: IMemberEmployee | undefined,
  namespace: NamespaceType | undefined,
  action: keyof Required<IPreferenceChannels>,
): boolean {
  if (!receiver) {
    return false;
  }

  if (!namespace) {
    return false;
  }

  const preference = receiver?.availablePreferences?.[namespace];

  return !!(preference?.enabled && preference?.channel?.[action]);
}

function getAutoPreference(
  receiver: IMemberEmployee | undefined,
  namespace: NamespaceType | undefined,
  action: keyof Required<IPreferenceChannels>,
): boolean {
  if (!receiver) {
    return false;
  }

  if (!namespace) {
    return false;
  }

  const preference = receiver?.availablePreferences?.[namespace];

  return !!(preference?.enabled && preference?.autoChannel?.[action]);
}

const ContactPage = () => {
  const { t } = useTranslation(["call"]);

  const history = useNavigate();

  const { id } = useParams<{ id: string }>();

  const { pathname } = useLocation();

  const mainRoute = new RegExp(`^\/contact\/${id}\/?$`).test(pathname);

  const disabled = !(id && pathname.endsWith(id));

  // usePageTracking({
  //   page: "contact",
  // });

  // const { trackEvent } = useEventTracking();

  const { data: accessEventData } = useQuery(
    [`access/event/${id}`],
    () =>
      client.request<{ accessEvent: IAccessEvent }>(QUERY_ACCESS_EVENT, { id }),
    {
      retry: false,
      staleTime: 1 * (60 * 1000),
      onError: () => {
        history("/", { replace: true });
      },
    },
  );

  const namespace = useMemo((): NamespaceType | undefined => {
    switch (accessEventData?.accessEvent?.type) {
      case "Food": {
        return "foodDelivery";
      }

      case "Package": {
        return "packageDelivery";
      }

      case "Guest":
      case "Visitor": {
        return "visitor";
      }

      case "MeetingAttendee": {
        return "meetingAttendee";
      }

      default: {
        return undefined;
      }
    }
  }, [accessEventData]);

  const receiver = accessEventData?.accessEvent?.receiver;

  const autoEmail = useMemo(
    () => getAutoPreference(receiver, namespace, "email"),
    [accessEventData],
  );
  const autoSMS = useMemo(
    () => getAutoPreference(receiver, namespace, "sms"),
    [accessEventData],
  );

  const availableForCall = useMemo(
    () => getPreference(receiver, namespace, "call"),
    [accessEventData],
  );

  const availableForPing = useMemo(
    () => getPreference(receiver, namespace, "ping"),
    [accessEventData],
  );

  const availableForSMS = useMemo(
    () => getPreference(receiver, namespace, "sms"),
    [accessEventData],
  );

  const availableForVideoCall = useMemo(
    () => getPreference(receiver, namespace, "videoCall"),
    [accessEventData],
  );

  useIdleEffect(
    () => {
      history("/", { replace: true });
    },
    20000,
    disabled,
  );

  return (
    <Curve>
      <div className="d-flex align-items-center flex-column">
        <BackButton to="/" />
        <Title disableAnimation={!mainRoute}>
          <Typography variant="h1">
            <Trans
              i18nKey={`call:title.${
                autoEmail || autoSMS ? "email-sms" : "no-email-sms"
              }`}
            />
          </Typography>
        </Title>
        {!!receiver?.phone && (
          <div className="call__buttons">
            <Bump disabled={!mainRoute}>
              <ContactButton
                disabled={!availableForCall}
                onClick={() => {
                  // trackEvent({ type: "contact_button_click", value: "call" });
                  history({
                    pathname: `call`,
                  });
                }}
              >
                <Typography variant="h3">{t("call:callBtn")}</Typography>
              </ContactButton>
            </Bump>
            <Bump disabled={!mainRoute}>
              <ContactButton
                disabled={!availableForPing}
                onClick={() => {
                  // trackEvent({ type: "contact_button_click", value: "ping" });
                  history({
                    pathname: `ping`,
                  });
                }}
              >
                <Typography variant="h3">{t("call:pingBtn")}</Typography>
              </ContactButton>
            </Bump>
            <Bump disabled={!mainRoute}>
              <ContactButton
                disabled={!availableForSMS}
                onClick={() => {
                  // trackEvent({ type: "contact_button_click", value: "sms" });
                  history({
                    pathname: `message`,
                  });
                }}
              >
                <Typography variant="h3">{t("call:smsBtn")}</Typography>
              </ContactButton>
            </Bump>
            <Bump disabled={!mainRoute}>
              <ContactButton
                disabled={!availableForVideoCall}
                onClick={() => {
                  // trackEvent({ type: "contact_button_click", value: "video" });
                  history({
                    pathname: "video",
                  });
                }}
              >
                <Typography variant="h3">{t("call:callVideoBtn")}</Typography>
              </ContactButton>
            </Bump>
          </div>
        )}
      </div>
      {!!receiver?.phone && (
        <Routes>
          <Route path="call" element={<Calling type="employee" />} />
          <Route path="ping" element={<Calling type="employee" />} />
          <Route path="message" element={<Message />} />
          <Route path="video" element={<VideoCalling />} />
        </Routes>
      )}
    </Curve>
  );
};

export default ContactPage;
